import { FC } from 'react';
import { TrophyIcon } from '@src/components/TrophyIcon';
import { CheckCircleIcon } from '@src/components/CheckCircleIcon';
import { CancelCircleIcon } from '@src/components/CancelCircleIcon';
import { Tooltip, TooltipTrigger, TooltipContent } from '@features/ui';
import { capitalize } from '@lib/utils';

interface AccountStatusCellProps {
  qualifyingStatus?: string | null;
  qualifyingStatusDetail?: string | null;
  qualifyingAccountName?: string | null;
}

export const AccountStatusCell: FC<AccountStatusCellProps> = ({
  qualifyingStatus,
  qualifyingStatusDetail,
  qualifyingAccountName,
}) => {
  const raw = String(qualifyingStatus ?? '');
  const status = raw.toLowerCase();
  const statusDetail = qualifyingStatusDetail || '';

  // Status messages mapping
  const getStatusMessage = (status: string): string => {
    if (status.includes('pending')) {
      return 'Trader has passed Eval, awaiting admin confirmation';
    }
    if (status.includes('failed')) {
      return statusDetail || 'Trader has passed Eval, admin did not approve';
    }
    if (status.includes('approved')) {
      return statusDetail || 'Trader has passed Eval, admin approved';
    }
    if (status.includes('complete')) {
      const accountName = qualifyingAccountName || '';

      return accountName
        ? `Converted to PA account: ${accountName}`
        : 'Complete';
    }
    if (status.includes('blocked')) {
      return 'Blocked';
    }
    if (status.includes('expired')) {
      return 'Expired';
    }
    if (status.includes('closed')) {
      return statusDetail || 'Closed';
    }

    return capitalize(raw);
  };

  const message = getStatusMessage(status);

  // If status indicates closed, show the red X icon
  if (status.includes('closed')) {
    return (
      <div className={'w-[180px] flex items-center gap-2'}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help">
              <CancelCircleIcon size={18} />
            </span>
          </TooltipTrigger>
          <TooltipContent className="text-white">{message}</TooltipContent>
        </Tooltip>
        <span className="sr-only">{message}</span>
      </div>
    );
  }

  // If status indicates complete, show the green checkmark icon
  if (status.includes('complete')) {
    return (
      <div className={'w-[180px] flex items-center gap-2'}>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex cursor-help">
              <CheckCircleIcon color="#a0c002" size={18} />
            </span>
          </TooltipTrigger>
          <TooltipContent className="text-white">{message}</TooltipContent>
        </Tooltip>
        <span className="sr-only">{message}</span>
      </div>
    );
  }

  const statusColorMap: Record<string, { color: string; opacity?: number }> = {
    pending: { color: '#fe8528' },
    approved: { color: '#fe8528' },
    failed: { color: '#cf1520' },
    expired: { color: '#ccc' },
    blocked: { color: '#000' },
    closed: { color: '#cf1520' },
  };

  // Determine which key to use based on status content
  let statusKey = status;

  if (status.includes('pending')) statusKey = 'pending';
  else if (status.includes('approved')) statusKey = 'approved';
  else if (status.includes('failed')) statusKey = 'failed';
  else if (status.includes('expired')) statusKey = 'expired';
  else if (status.includes('blocked')) statusKey = 'blocked';
  else if (status.includes('closed')) statusKey = 'closed';

  const meta: { color: string; opacity?: number } = statusColorMap[
    statusKey
  ] ?? { color: '#ccc' };

  return (
    <div className={'w-[180px] flex items-center gap-2'}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-help">
            <TrophyIcon
              className={meta.opacity !== 1 ? 'opacity-50' : ''}
              fill={meta.color}
              size={18}
            />
          </span>
        </TooltipTrigger>
        {message && (
          <TooltipContent className="text-white">{message}</TooltipContent>
        )}
      </Tooltip>
      {/* Screen-reader label */}
      <span className="sr-only">{message}</span>
    </div>
  );
};
